import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Info, ExternalLink } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { useSandboxApi } from './config/sandbox';
import { SANDBOX_DOCS_URL } from '../../utils/environment';
import DataTable from '../../components/ui/DataTable';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';

// Entegrasyonlar > Test Ortamı (sandbox self-servis giriş; tasarım belgesi
// docs/superpowers/specs/2026-10-07-sandbox-self-servis-giris-design.md, K2/K4).
//
// Canlı panel test ortamının kimlik sağlayıcısıdır: "Test Ortamına Git" 2 dk geçerli, tek
// kullanımlık bir geçiş belgesi üretir ve test ortamının /sandbox/enter sayfasını YENİ SEKMEDE açar
// (belge adresin # parçasında taşınır). Liste, bu şirketten test ortamına kimin ne zaman girdiğinin
// günlüğüdür — test ortamında herkes aynı İK Müdürü hesabını paylaştığı için "kim girdi" yalnız
// burada bilinir.
//
// Liste ApiKeysTab aynasıdır: başlık ve eylem düğmesi DataTable'ın İÇİNDE; veri girişi yok (çekmece
// ya da modal yok).
const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* İç içe scroll yasak — tek kaydırma DataTable satırlarıdır. */
  min-height: 0;
`;

const Notice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  border-left: 3px solid ${({ theme }) => theme.colors.primary.main};
  background: #EEF6FD;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.4;

  > svg { flex-shrink: 0; margin-top: 1px; color: ${({ theme }) => theme.colors.primary.main}; }
`;

const NoticeBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const StrongLine = styled.span`
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const InfoRow = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const Code = styled.code`
  font-family: monospace;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.surface.card};
  padding: 2px 6px;
  border-radius: 4px;
  word-break: break-all;
`;

const DocsLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.main};
  text-decoration: none;

  &:hover { text-decoration: underline; }
`;

// Test anahtarlarının öneki (canlı anahtarlar flx_live_ ile başlar) — çeviri değil, sabit.
const TEST_KEY_PREFIX = 'flx_test_';

export default function SandboxTab() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const {
    fetching, failed, info, data, columns, createHandoff,
  } = useSandboxApi();

  // Çift tık iki belge üretmesin: istek sürerken düğme yeni istek başlatmaz. Ref (state değil):
  // ikinci tık aynı render içinde gelebilir.
  const inFlight = useRef(false);

  const handleGo = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    // POPUP ENGELLEYİCİ: tarayıcı yalnız kullanıcı hareketinin SENKRON akışında açılan pencereye
    // izin verir — await'ten sonra açılan sekme engellenirdi. Boş sekme ŞİMDİ açılır, adres belge
    // gelince yazılır. 'noopener' özelliği verilmez (verilirse window.open null döner); karşı
    // pencere bağı adres yazılmadan önce elle koparılır.
    const popup = window.open('', '_blank');
    try {
      const handoff = await createHandoff();
      if (!handoff) {
        if (popup) popup.close();   // hata toast'ı interceptor'da
        return;
      }
      if (popup) {
        popup.opener = null;
        popup.location.href = handoff.url;
      } else {
        // Sekme engellendi: aynı sekmede devam (belge 2 dk geçerli, tek kullanımlık).
        window.location.assign(handoff.url);
      }
    } finally {
      inFlight.current = false;
    }
  }, [createHandoff]);

  const unavailable = info?.available === false;
  const canGo = Boolean(canWrite('PA')) && info?.available === true;
  const docsUrl = info?.docsUrl || SANDBOX_DOCS_URL;
  // "Yüklenemedi" ile "hiç giriş yok" AYRI: yükleme başarısızsa boş-durum metni söylenmez.
  const isEmpty = !fetching && !failed && data.length === 0;

  return (
    <Container>
      {failed && <LoadErrorBanner message={t('sandboxEnv.loadFailed')} />}

      <Notice data-role="sandbox-notice">
        <Info size={16} />
        <NoticeBody>
          {unavailable && <StrongLine data-role="sandbox-unavailable">{t('sandboxEnv.unavailable')}</StrongLine>}
          {isEmpty && <StrongLine data-role="sandbox-empty">{t('sandboxEnv.empty')}</StrongLine>}
          <span>{t('sandboxEnv.noticeIntro')}</span>
          <span>{t('sandboxEnv.noticeReset')}</span>
          <span>{t('sandboxEnv.noticeApiKey')}</span>
          <InfoRow>
            <span>{`${t('sandboxEnv.keyPrefix')}:`}</span>
            <Code data-role="sandbox-key-prefix">{TEST_KEY_PREFIX}</Code>
          </InfoRow>
          {info?.sandboxApiUrl && (
            <InfoRow>
              <span>{`${t('sandboxEnv.apiRoot')}:`}</span>
              <Code data-role="sandbox-api-url">{info.sandboxApiUrl}</Code>
            </InfoRow>
          )}
          <span data-role="sandbox-shared-account">{t('sandboxEnv.noticeSharedAccount')}</span>
          <DocsLink data-role="sandbox-docs" href={docsUrl} target="_blank" rel="noopener noreferrer">
            {t('sandboxEnv.docsLink')}
            <ExternalLink size={14} />
          </DocsLink>
        </NoticeBody>
      </Notice>

      <DataTable
        title={t('sandboxEnv.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        serverSide={false}
        disableFilters
        canCreateNewRecord={canGo}
        newButtonLabel={t('sandboxEnv.goButton')}
        newButtonPlusSign={false}
        onNewButtonClick={handleGo}
        tableBodyMaxHeight="calc(100vh - 460px)"
      />
    </Container>
  );
}
